// Orchestrator Phase 10 GitNexus Setup — compose phase 01-05 thành fail-soft flow.
// Gọi sau `runAiSetupPhase` trong `finalizeWorkspaceScaffold`.
//
// Try/catch toàn bộ — KHÔNG throw. Workspace OK dù GitNexus fail:
//   - User decline install → return { ok: false, reason: "user-declined" }
//   - Install fail → recovery menu (retry/skip/abort) — abort throw UserAbortedRecoveryError
//   - Setup/analyze fail → recovery menu
//   - Wiki fail → log warn nhưng continue (wiki không critical)
//   - MCP register fail → log warn nhưng continue (user thấy workspace work)
//
// Audit log: gitnexus_setup,result=ok/skipped/failed,reason=...
import { appendAuditEntry } from "./audit-log-appender.js";
import { ensureGitnexusInstalledAndLatest } from "./ensure-gitnexus-installed-and-latest.js";
import {
  promptRetryOrSkip,
  UserAbortedRecoveryError,
} from "./prompt-recovery-action-on-failure.js";
import { registerGitnexusMcpServer } from "./register-gitnexus-mcp-server.js";
import {
  GitnexusOperationError,
  runGitnexusAnalyze,
  runGitnexusSetup,
} from "./run-gitnexus-setup-and-analyze.js";
import { runGitnexusWikiConditional } from "./run-gitnexus-wiki-conditional.js";
import { log } from "./terminal-logger.js";

export interface GitnexusSetupArgs {
  workspacePath: string;
  // v3: init KHÔNG analyze (workspace src/ rỗng → index vô nghĩa). Chỉ install +
  // setup skills + MCP register. Analyze chạy per-repo khi `avatar add repo`.
  skipAnalyze?: boolean;
}

export interface GitnexusSetupResult {
  ok: boolean;
  installed: boolean; // gitnexus binary có hay chưa
  analyzed: boolean; // .gitnexus/ created
  wikiGenerated: boolean; // wiki/index.html created
  mcpRegistered: boolean;
  reason?: string;
}

// Helper — wrap analyze call với recovery menu.
async function analyzeWithRecovery(workspacePath: string): Promise<boolean> {
  while (true) {
    try {
      await runGitnexusAnalyze(workspacePath);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const hint =
        err instanceof GitnexusOperationError && err.reason === "missing-output"
          ? "Repo có thể empty hoặc gitnexus version mismatch. Check `gitnexus --version`."
          : "Network glitch? Retry thường work.";
      const action = await promptRetryOrSkip({
        taskName: "GitNexus analyze workspace",
        reason: message,
        allowSkip: true,
        hint,
      });
      if (action === "abort") {
        throw new UserAbortedRecoveryError("User abort tại bước GitNexus analyze.");
      }
      if (action === "skip") return false;
    }
  }
}

// Main orchestrator. Fail-soft semantic — return result, không throw (trừ user abort).
export async function runGitnexusSetupPhase(args: GitnexusSetupArgs): Promise<GitnexusSetupResult> {
  const result: GitnexusSetupResult = {
    ok: false,
    installed: false,
    analyzed: false,
    wikiGenerated: false,
    mcpRegistered: false,
  };

  try {
    log.info("=== Phase 10: GitNexus Setup ===");

    // Step 1 — Cài MẶC ĐỊNH + giữ bản mới nhất (KHÔNG hỏi consent — GitNexus là
    // hạ tầng cốt lõi). Fail-soft: cài/update lỗi → warn + tiếp (workspace vẫn OK).
    const ens = await ensureGitnexusInstalledAndLatest();
    if (!ens.installed) {
      await appendAuditEntry("gitnexus_setup", `result=skipped,reason=${ens.reason}`);
      result.reason = ens.reason;
      return result;
    }
    result.installed = true;
    log.success(`GitNexus available${ens.version ? ` v${ens.version}` : ""}`);

    // Step 2 — `gitnexus setup` (global skills)
    // Wrap try/catch — setup fail không critical (skills không phải core), continue.
    try {
      await runGitnexusSetup();
    } catch (err) {
      log.warn(`gitnexus setup fail: ${(err as Error).message}`);
      log.dim("Skip global skills install. Workspace vẫn dùng được.");
    }

    // v3: skipAnalyze (init mỏng) → bỏ Step 3 (analyze) + Step 4 (wiki). Chỉ
    // install + skills + MCP. Analyze + wiki chạy per-repo khi `avatar add repo`.
    if (args.skipAnalyze) {
      log.dim("GitNexus: cài + MCP xong. Index sẽ chạy per-repo khi `avatar add repo`.");
    } else {
      // Step 3 — `gitnexus analyze .` (workspace root)
      const analyzed = await analyzeWithRecovery(args.workspacePath);
      if (!analyzed) {
        await appendAuditEntry("gitnexus_setup", "result=skipped,reason=analyze-skipped");
        log.dim("Skip analyze. GitNexus installed nhưng chưa index.");
        result.reason = "analyze-skipped";
        return result;
      }
      result.analyzed = true;

      // Step 4 — Wiki conditional (LLMLite mode only)
      const wikiResult = await runGitnexusWikiConditional(args.workspacePath);
      result.wikiGenerated = wikiResult.ran;
      if (wikiResult.skipped && wikiResult.reason === "fail") {
        log.warn(`Wiki gen fail (workspace vẫn OK): ${wikiResult.detail ?? "unknown"}`);
      }
    }

    // Step 5 — Register MCP server
    try {
      const mcpResult = await registerGitnexusMcpServer();
      result.mcpRegistered = true;
      if (!mcpResult.wasUpdated) {
        log.dim("MCP server gitnexus đã registered trước đó.");
      }
    } catch (err) {
      log.warn(`MCP server register fail: ${(err as Error).message}`);
      log.dim(
        "Workspace OK nhưng Claude Code không tự attach MCP server. Manual add vào ~/.claude/mcp_servers.json.",
      );
    }

    result.ok = true;
    await appendAuditEntry(
      "gitnexus_setup",
      `result=ok,analyzed=${result.analyzed},wiki=${result.wikiGenerated},mcp=${result.mcpRegistered}`,
    );
    log.success("GitNexus ready");
    return result;
  } catch (err) {
    // User abort → re-throw để init.ts catch + exit 0 graceful (consistent với pattern).
    if (err instanceof UserAbortedRecoveryError) throw err;

    const message = err instanceof Error ? err.message : String(err);
    log.warn(`GitNexus setup thất bại: ${message}`);
    log.dim("Workspace vẫn sẵn sàng. Setup sau qua `avatar gitnexus install`.");
    await appendAuditEntry("gitnexus_setup", `result=failed,error=${message.slice(0, 200)}`);
    result.reason = message;
    return result;
  }
}
